import { API_CONFIG } from '../config';
import { Filters } from '../components/FilterBar';
import { Language } from '../locales';

export const BIDDING_SHOTS_PER_PAGE_DEFAULT = 50;
export const BIDDING_SHOTS_PER_PAGE_MIN = 1;
export const BIDDING_SHOTS_PER_PAGE_MAX = 200;

export const USER_PREFERENCES_CACHE_KEY = 'spellbook_user_preferences_cache';

export interface UserPreferencesViewModes {
  artists?: 'grid' | 'list';
  projects?: 'grid' | 'list';
  bidding?: 'grid' | 'list';
}

export interface UserPreferencesSort {
  artists?: string;
  projects?: string;
  teams?: string;
  bidding?: string;
}

export interface UserPreferences {
  language?: Language;
  theme?: 'dark' | 'light';
  customTheme?: Record<string, unknown> | null;
  uiScale?: number;
  viewModes?: UserPreferencesViewModes;
  biddingShotsPerPage?: number;
  financeSelectedProjectId?: string | null;
  biddingActiveTable?: 'revenue' | 'expense';
  biddingAggregationMode?: 'specialization' | 'level';
  /** Value column in ShotTable: hours or fixed sum */
  biddingValueColumnMode?: 'hours' | 'sum';
  sort?: UserPreferencesSort;
  filters?: Partial<Filters>;
  /** Admin only: when true, see all projects; default false = only assigned */
  adminSeeAllProjects?: boolean;
  /** Hidden ShotTable column IDs keyed by bidding sheet ID */
  biddingHiddenColumns?: Record<string, string[]>;
}

type Listener = (prefs: UserPreferences) => void;

let preferencesReadyForSync = false;
let currentPrefs: UserPreferences = {};
let pendingPatch: Partial<UserPreferences> = {};
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<Listener>();

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function deepMerge<T extends Record<string, unknown>>(target: T, patch: Partial<T>): T {
  const result = { ...target } as T;
  for (const key of Object.keys(patch) as Array<keyof T>) {
    const val = patch[key];
    const existing = result[key];
    if (isPlainObject(val) && isPlainObject(existing)) {
      result[key] = deepMerge(
        existing as Record<string, unknown>,
        val as Record<string, unknown>
      ) as T[keyof T];
    } else if (val !== undefined) {
      result[key] = val as T[keyof T];
    }
  }
  return result;
}

export function clampBiddingShotsPerPage(value: number): number {
  if (!Number.isFinite(value)) return BIDDING_SHOTS_PER_PAGE_DEFAULT;
  return Math.min(BIDDING_SHOTS_PER_PAGE_MAX, Math.max(BIDDING_SHOTS_PER_PAGE_MIN, Math.round(value)));
}

function readLegacyPreferences(): UserPreferences {
  const prefs: UserPreferences = {};

  const lang = localStorage.getItem('spellbook_language');
  if (lang === 'ru' || lang === 'en') prefs.language = lang;

  const theme = localStorage.getItem('spellbook_theme');
  if (theme === 'dark' || theme === 'light') prefs.theme = theme;

  const scale = localStorage.getItem('spellbook_ui_scale');
  if (scale) {
    const parsed = parseFloat(scale);
    if (Number.isFinite(parsed)) prefs.uiScale = parsed;
  }

  const customThemeRaw = localStorage.getItem('spellbook_custom_theme');
  if (customThemeRaw) {
    try {
      prefs.customTheme = JSON.parse(customThemeRaw);
    } catch {
      // ignore
    }
  }

  const viewModes: UserPreferencesViewModes = {};
  const artists = localStorage.getItem('spellbook-view-mode');
  if (artists === 'grid' || artists === 'list') viewModes.artists = artists;
  const projects = localStorage.getItem('spellbook-project-view-mode');
  if (projects === 'grid' || projects === 'list') viewModes.projects = projects;
  const bidding = localStorage.getItem('spellbook-bidding-view-mode');
  if (bidding === 'grid' || bidding === 'list') viewModes.bidding = bidding;
  if (Object.keys(viewModes).length > 0) prefs.viewModes = viewModes;

  const shotsRaw = localStorage.getItem('spellbook-bidding-shots-per-page');
  if (shotsRaw) {
    const parsed = parseInt(shotsRaw, 10);
    if (!Number.isNaN(parsed)) prefs.biddingShotsPerPage = clampBiddingShotsPerPage(parsed);
  }

  const financeProject = localStorage.getItem('spellbook_finance_selected_project');
  if (financeProject) prefs.financeSelectedProjectId = financeProject;

  const cacheRaw = localStorage.getItem(USER_PREFERENCES_CACHE_KEY);
  if (cacheRaw) {
    try {
      const cached = JSON.parse(cacheRaw) as UserPreferences;
      return deepMerge(prefs as Record<string, unknown>, cached as Record<string, unknown>) as UserPreferences;
    } catch {
      // ignore
    }
  }

  return prefs;
}

function writeLegacyStorage(prefs: UserPreferences): void {
  if (prefs.language) localStorage.setItem('spellbook_language', prefs.language);
  if (prefs.theme) localStorage.setItem('spellbook_theme', prefs.theme);
  if (prefs.uiScale !== undefined) localStorage.setItem('spellbook_ui_scale', String(prefs.uiScale));
  if (prefs.customTheme === null) {
    localStorage.removeItem('spellbook_custom_theme');
  } else if (prefs.customTheme) {
    localStorage.setItem('spellbook_custom_theme', JSON.stringify(prefs.customTheme));
  }
  if (prefs.viewModes?.artists) localStorage.setItem('spellbook-view-mode', prefs.viewModes.artists);
  if (prefs.viewModes?.projects) localStorage.setItem('spellbook-project-view-mode', prefs.viewModes.projects);
  if (prefs.viewModes?.bidding) localStorage.setItem('spellbook-bidding-view-mode', prefs.viewModes.bidding);
  if (prefs.biddingShotsPerPage !== undefined) {
    localStorage.setItem('spellbook-bidding-shots-per-page', String(prefs.biddingShotsPerPage));
  }
  if (prefs.financeSelectedProjectId) {
    localStorage.setItem('spellbook_finance_selected_project', prefs.financeSelectedProjectId);
  } else if (prefs.financeSelectedProjectId === null) {
    localStorage.removeItem('spellbook_finance_selected_project');
  }
  localStorage.setItem(USER_PREFERENCES_CACHE_KEY, JSON.stringify(prefs));
}

export function applyPreferencesToDom(prefs: UserPreferences): void {
  const theme = prefs.theme ?? 'dark';
  document.documentElement.className = theme;

  const scale = prefs.uiScale ?? 1;
  document.documentElement.style.setProperty('--ui-scale', String(scale));

  if (prefs.language) {
    document.documentElement.lang = prefs.language;
  }

  if (prefs.customTheme && typeof prefs.customTheme === 'object') {
    try {
      const root = document.documentElement;
      const themeObj = prefs.customTheme as Record<string, string>;
      for (const [key, value] of Object.entries(themeObj)) {
        if (typeof value === 'string' && key.startsWith('--')) {
          root.style.setProperty(key, value);
        }
      }
    } catch {
      // ignore custom theme apply errors
    }
  }
}

export function getUserPreferences(): UserPreferences {
  if (Object.keys(currentPrefs).length === 0) {
    currentPrefs = readLegacyPreferences();
  }
  return { ...currentPrefs };
}

export function subscribeUserPreferences(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyListeners(): void {
  const snapshot = getUserPreferences();
  listeners.forEach((fn) => fn(snapshot));
}

export async function flushUserPreferencesToServer(): Promise<void> {
  if (Object.keys(pendingPatch).length === 0) return;

  const token = localStorage.getItem('spellbook_token');
  if (!token) {
    pendingPatch = {};
    return;
  }

  const body = { ...pendingPatch };
  pendingPatch = {};

  try {
    const response = await fetch(`${API_CONFIG.API_URL}/users/me/preferences`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    if (response.ok) {
      const data = await response.json();
      if (data.preferences && typeof data.preferences === 'object') {
        const before = prefsSnapshot(currentPrefs);
        currentPrefs = deepMerge(currentPrefs as Record<string, unknown>, data.preferences) as UserPreferences;
        if (prefsSnapshot(currentPrefs) !== before) {
          writeLegacyStorage(currentPrefs);
          notifyListeners();
        }
      }
    }
  } catch (error) {
    console.warn('Failed to save user preferences:', error);
    pendingPatch = deepMerge(pendingPatch as Record<string, unknown>, body as Record<string, unknown>) as Partial<UserPreferences>;
  }
}

function prefsSnapshot(prefs: UserPreferences): string {
  return JSON.stringify(prefs);
}

export function updateUserPreferences(patch: Partial<UserPreferences>, options?: { immediate?: boolean }): UserPreferences {
  const before = prefsSnapshot(currentPrefs);
  currentPrefs = deepMerge(
    currentPrefs as Record<string, unknown>,
    patch as Record<string, unknown>
  ) as UserPreferences;
  const after = prefsSnapshot(currentPrefs);

  if (before === after) {
    return getUserPreferences();
  }

  writeLegacyStorage(currentPrefs);
  applyPreferencesToDom(currentPrefs);
  notifyListeners();

  pendingPatch = deepMerge(
    pendingPatch as Record<string, unknown>,
    patch as Record<string, unknown>
  ) as Partial<UserPreferences>;

  if (options?.immediate) {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = null;
    flushUserPreferencesToServer();
  } else {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      flushUserPreferencesToServer();
    }, 400);
  }

  return getUserPreferences();
}

export function isPreferencesReadyForSync(): boolean {
  return preferencesReadyForSync;
}

export async function loadUserPreferencesFromServer(): Promise<UserPreferences> {
  preferencesReadyForSync = false;
  const token = localStorage.getItem('spellbook_token');
  const local = readLegacyPreferences();
  currentPrefs = local;

  if (!token) {
    applyPreferencesToDom(currentPrefs);
    notifyListeners();
    preferencesReadyForSync = true;
    return getUserPreferences();
  }

  try {
    const response = await fetch(`${API_CONFIG.API_URL}/users/me/preferences`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      const data = await response.json();
      if (data.preferences && typeof data.preferences === 'object') {
        currentPrefs = deepMerge(
          local as Record<string, unknown>,
          data.preferences as Record<string, unknown>
        ) as UserPreferences;
      }
    }
  } catch (error) {
    console.warn('Failed to load user preferences from server:', error);
  }

  writeLegacyStorage(currentPrefs);
  applyPreferencesToDom(currentPrefs);
  preferencesReadyForSync = true;
  notifyListeners();
  return getUserPreferences();
}

export function readBiddingShotsPerPage(): number {
  const prefs = getUserPreferences();
  if (prefs.biddingShotsPerPage !== undefined) {
    return clampBiddingShotsPerPage(prefs.biddingShotsPerPage);
  }
  const raw = localStorage.getItem('spellbook-bidding-shots-per-page');
  const parsed = parseInt(raw ?? '', 10);
  if (!Number.isNaN(parsed)) return clampBiddingShotsPerPage(parsed);
  return BIDDING_SHOTS_PER_PAGE_DEFAULT;
}

export function writeBiddingShotsPerPage(value: number): void {
  const clamped = clampBiddingShotsPerPage(value);
  updateUserPreferences({ biddingShotsPerPage: clamped });
}
